import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Action, Chip, EmptyState, ErrorNote, FilterChips, Loading, Panel, PanelHead } from '../aa';
import { aiRuntimeApi, type RuntimeUsage, type RuntimeUsagePeriod } from '../../services/aiRuntimeApi';
import { PERIODS, sinceLabel } from './usageParts';

/*
 * API usage: what the company's AI Runtime keys called and what it cost,
 * at the price the company pays (margin included). The same calls are part of
 * the agent usage on Plan & payments, drawn from the same prepaid balance.
 */

const count = (n: number) => n.toLocaleString();

const calls = (n: number) => {
  if (n === 1) return '1 call';
  return `${count(n)} calls`;
};

const exactUsd = (value: number) => {
  if (value === 0) return '$0.00';
  if (value >= 0.01) return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${value.toFixed(6).replace(/0+$/, '')}`;
};

const when = (iso: string) => new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
});

const shortModel = (model: string) => model.replace(/^accounts-fireworks-models-/, '');

const keysUsed = (n: number) => {
  if (n === 1) return '1 key used';
  return `${n} keys used`;
};

const Figure: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div className="rounded-act bg-canvas-inset px-4 py-3">
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-dim">{label}</div>
    <div className="mt-1 text-[20px] font-bold tabular-nums text-ink">{value}</div>
    {note && <div className="text-[12px] text-ink-dim">{note}</div>}
  </div>
);

const lastDays = (daily: RuntimeUsage['daily']) => {
  const byDay = new Map(daily.map((d) => [d.day, d]));
  const days = [];
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ key, usd: byDay.get(key)?.usd || 0, calls: byDay.get(key)?.calls || 0 });
  }
  return days;
};

const DailyBars: React.FC<{ daily: RuntimeUsage['daily'] }> = ({ daily }) => {
  const days = lastDays(daily);
  const max = Math.max(...days.map((d) => d.usd));
  return (
    <div>
      <div className="flex h-16 items-end gap-1" role="img" aria-label="API spend per day, last 14 days">
        {days.map((d) => {
          let height = 0;
          if (max > 0) height = Math.max((d.usd / max) * 100, d.usd > 0 ? 6 : 0);
          return (
            <span key={d.key} className="flex h-full flex-1 items-end rounded-[3px] bg-canvas-inset"
              title={`${d.key}: ${exactUsd(d.usd)}, ${calls(d.calls)}`}>
              <span className="w-full rounded-[3px] bg-ink" style={{ height: `${height}%` }} />
            </span>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-ink-dim">
        <span>14 days ago</span><span>Today</span>
      </div>
    </div>
  );
};

const LIST = 'm-0 max-h-[272px] list-none divide-y divide-line-soft overflow-y-auto rounded-act border border-line-soft p-0';

const Pager: React.FC<{ page: number; pageSize: number; total: number; onPage: (page: number) => void }> = ({
  page, pageSize, total, onPage,
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <span className="text-[12px] tabular-nums text-ink-dim">
        Showing {count(first)}–{count(last)} of {count(total)} calls
      </span>
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Action size="sm" variant="outline" disabled={page <= 1} reason="This is the first page" onClick={() => onPage(page - 1)}>
            Previous
          </Action>
          <span className="text-[12px] tabular-nums text-ink-dim">Page {page} of {pages}</span>
          <Action size="sm" variant="outline" disabled={page >= pages} reason="This is the last page" onClick={() => onPage(page + 1)}>
            Next
          </Action>
        </div>
      )}
    </div>
  );
};

const Share: React.FC<{ value: number; total: number }> = ({ value, total }) => {
  let width = 0;
  if (total > 0) width = Math.max((value / total) * 100, value > 0 ? 3 : 0);
  return (
    <span className="flex h-1.5 w-24 overflow-hidden rounded-full bg-canvas-inset">
      <span className="h-full rounded-full bg-ink" style={{ width: `${width}%` }} />
    </span>
  );
};

export const ApiUsagePanel: React.FC = () => {
  const [period, setPeriod] = useState<RuntimeUsagePeriod>('period');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RuntimeUsage | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);

  const load = useCallback((chosen: RuntimeUsagePeriod, chosenPage: number) => {
    latest.current += 1;
    const request = latest.current;
    setLoading(true);
    setFailed(false);
    aiRuntimeApi.usage(chosen, chosenPage)
      .then((next) => {
        if (request === latest.current) setData(next);
      })
      .catch(() => {
        if (request === latest.current) setFailed(true);
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
  }, []);

  useEffect(() => { load(period, page); }, [load, period, page]);

  const choosePeriod = (next: RuntimeUsagePeriod) => {
    setPage(1);
    setPeriod(next);
  };

  let body: React.ReactNode = null;
  if (loading && !data) body = <Loading label="Loading API usage" />;
  if (!loading && failed) {
    body = (
      <div className="px-6 pb-6">
        <ErrorNote message="Could not load API usage." />
        <div className="mt-3"><Action size="sm" variant="outline" onClick={() => load(period, page)}>Try again</Action></div>
      </div>
    );
  }
  if (!failed && data && !data.available) {
    body = <div className="px-6 pb-6 text-[13px] text-ink-dim">Usage figures are being refreshed. Check back in a minute.</div>;
  }
  if (!failed && data?.available && data.calls === 0) {
    body = (
      <div className="px-6 pb-6">
        <EmptyState title={`No API calls ${sinceLabel(data.period, data.since)}`}
          reason="Calls your apps make with AI Runtime keys show up here. Create a key in Settings, AI Runtime." />
      </div>
    );
  }
  if (!failed && data?.available && data.calls > 0) {
    body = (
      <div className={`space-y-6 px-6 pb-6 transition-opacity ${loading ? 'opacity-50' : ''}`}>
        <div className="grid gap-3 sm:grid-cols-3">
          <Figure label="Spent" value={exactUsd(data.total_usd)} note={`From your prepaid balance, ${sinceLabel(data.period, data.since)}`} />
          <Figure label="Calls" value={count(data.calls)} note={keysUsed(data.by_key.length)} />
          <Figure label="Tokens" value={count(data.input_tokens + data.output_tokens)} note={`${count(data.input_tokens)} in, ${count(data.output_tokens)} out`} />
        </div>

        <DailyBars daily={data.daily} />

        <div className="grid gap-6 lg:grid-cols-2">
          <section>
            <h3 className="m-0 mb-2 text-[13px] font-semibold text-ink">By key</h3>
            <ul className={LIST}>
              {data.by_key.map((k) => (
                <li key={k.apiKeyId} className="flex items-center gap-3 px-4 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-[13.5px] font-semibold text-ink">
                      <span className="truncate">{k.name}</span>
                      {k.revoked && <Chip>Revoked</Chip>}
                    </span>
                    {k.prefix && <code className="mt-0.5 block w-fit text-[11.5px]">{k.prefix}…</code>}
                    <span className="mt-0.5 block text-[12px] text-ink-dim">{calls(k.calls)} · last {when(k.lastAt)}</span>
                  </span>
                  <Share value={k.usd} total={data.total_usd} />
                  <span className="w-20 text-right text-[13.5px] font-semibold tabular-nums text-ink">{exactUsd(k.usd)}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="m-0 mb-2 text-[13px] font-semibold text-ink">By model</h3>
            <ul className={LIST}>
              {data.by_model.map((m) => (
                <li key={m.model} className="flex items-center gap-3 px-4 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-ink">{shortModel(m.model)}</span>
                    <span className="block text-[12px] text-ink-dim">{calls(m.calls)}</span>
                  </span>
                  <Share value={m.usd} total={data.total_usd} />
                  <span className="w-20 text-right text-[13.5px] font-semibold tabular-nums text-ink">{exactUsd(m.usd)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section>
          <h3 className="m-0 mb-2 text-[13px] font-semibold text-ink">Latest calls</h3>
          <div className="overflow-x-auto rounded-act border border-line-soft">
            <table className="w-full min-w-[560px] border-collapse text-left text-[12.5px]">
              <thead className="bg-canvas-inset text-[11px] uppercase tracking-[0.06em] text-ink-dim">
                <tr>
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Key</th>
                  <th className="px-4 py-2 font-semibold">Asked for</th>
                  <th className="px-4 py-2 font-semibold">Answered by</th>
                  {/* <th className="px-4 py-2 text-right font-semibold">Tokens</th> */}
                  <th className="px-4 py-2 text-right font-semibold">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {data.recent.map((r) => {
                  let picker = 'You';
                  if (r.routedBy === 'jev') picker = 'Jev';
                  if (r.routedBy === 'fallback') picker = 'Backup';
                  return (
                    <tr key={`${r.at}-${r.model}-${r.usd}`}>
                      <td className="whitespace-nowrap px-4 py-2 text-ink-muted">{when(r.at)}</td>
                      <td className="px-4 py-2 text-ink">{r.key_name}</td>
                      <td className="px-4 py-2"><code className="text-ink">{r.requestedModel || 'jev/auto'}</code></td>
                      <td className="px-4 py-2 text-ink">
                        {shortModel(r.model)} <span className="text-ink-dim">· picked by {picker}</span>
                      </td>
                      {/* <td className="px-4 py-2 text-right tabular-nums text-ink-muted">{count(r.inputTokens)} / {count(r.outputTokens)}</td> */}
                      <td className="px-4 py-2 text-right font-semibold tabular-nums text-ink">{exactUsd(r.usd)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.page_size} total={data.calls} onPage={setPage} />
        </section>
      </div>
    );
  }

  return (
    <Panel>
      <PanelHead
        kicker="AI Runtime"
        title="API usage"
        desc="What your apps used through your AI Runtime keys, at the price you pay. It comes out of the same prepaid balance as your agent usage."
      />
      <div className="px-6 pb-4">
        <FilterChips
          tabs={PERIODS.map((p) => ({ id: p.id, label: p.label }))}
          active={period}
          onChange={(id) => choosePeriod(id as RuntimeUsagePeriod)}
          label="Usage period"
        />
      </div>
      {body}
    </Panel>
  );
};

export default ApiUsagePanel;
